/** P32 (Feb 27, 2026) — ODM SFTP (Global) tab.
 *
 * Read-only display of AZAI's shared SFTP connection to Ohio Medicaid's
 * EDI gateway, plus a "Test Connection" button. Credentials are managed
 * via .env / server deployment dashboard — this view exists for audit
 * + connectivity verification.
 */
import React from 'react';
import {
  CheckCircle, Lock, Plug, WarningCircle,
} from '@phosphor-icons/react';

import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

import { ReadOnlyField, StatusBadge } from './sharedUi';

const SftpTestResult = ({ result }) => {
  if (!result) return null;
  const ok = Boolean(result.success);
  return (
    <div
      className={`flex items-center gap-2 text-sm ${ok ? 'text-emerald-700' : 'text-rose-700'}`}
      data-testid="sftp-test-result"
    >
      {ok ? (
        <>
          <CheckCircle size={16} weight="fill" /> Connected successfully
          {result.files_listed ? ` · ${result.files_listed} files visible` : ''}
        </>
      ) : (
        <>
          <WarningCircle size={16} weight="fill" /> {result.error || 'Unknown failure'}
        </>
      )}
    </div>
  );
};

const OdmSftpTab = ({ creds, credsLoading, sftpTest, onTest }) => (
  <Card className="border-slate-200" data-testid="sftp-card">
    <CardHeader>
      <div className="flex items-start justify-between">
        <div>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Plug size={20} /> ODM SFTP (Global)
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            AZAI&apos;s shared connection to Ohio Medicaid&apos;s EDI gateway. Used for every agency&apos;s 837P submissions and 999/277CA/835 responses.
          </p>
        </div>
        {creds?.sftp ? <StatusBadge ok={creds.sftp.configured} /> : null}
      </div>
    </CardHeader>
    <CardContent className="space-y-6">
      {credsLoading || !creds ? (
        <p className="text-sm text-slate-500 italic">Loading credentials…</p>
      ) : (
        <>
          <div className="grid md:grid-cols-3 gap-4">
            <ReadOnlyField label="Trading Partner ID" value={creds.submitter.trading_partner_id} testid="sftp-tpid" />
            <ReadOnlyField label="Submitter Name" value={creds.submitter.submitter_name} testid="sftp-submitter-name" mono={false} />
            <ReadOnlyField label="Environment" value={creds.sftp.environment?.toUpperCase()} testid="sftp-env" />
            <ReadOnlyField label="Active Host" value={creds.sftp.active_host} testid="sftp-host" />
            <ReadOnlyField label="Port" value={creds.sftp.port} testid="sftp-port" />
            <ReadOnlyField label="Username" value={creds.sftp.username} testid="sftp-username" />
            <ReadOnlyField label="Auth Method" value={creds.sftp.auth_method} testid="sftp-auth-method" />
            <ReadOnlyField label="Password" value={creds.sftp.password_masked} testid="sftp-password" />
            <ReadOnlyField label="Private Key Path" value={creds.sftp.private_key_path} testid="sftp-key-path" />
            <ReadOnlyField label="Inbound Dir (uploads)" value={creds.sftp.inbound_dir} testid="sftp-inbound" />
            <ReadOnlyField label="Outbound Dir (downloads)" value={creds.sftp.outbound_dir} testid="sftp-outbound" />
            <ReadOnlyField label="CERT Host" value={creds.sftp.host_cert} testid="sftp-host-cert" />
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-md p-4 text-xs text-slate-600 flex items-start gap-2">
            <Lock size={16} className="mt-0.5 shrink-0" />
            <div>
              <strong>Credentials managed via environment variables.</strong> To change any value, set the matching key on the deployment dashboard and redeploy. Read-only here for security.
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={onTest} disabled={sftpTest.running} className="bg-indigo-900 hover:bg-indigo-800" data-testid="test-sftp-btn">
              {sftpTest.running ? 'Testing…' : 'Test SFTP Connection'}
            </Button>
            <SftpTestResult result={sftpTest.result} />
          </div>
        </>
      )}
    </CardContent>
  </Card>
);

export default OdmSftpTab;
